import { createGlobalState, useTimeoutFn } from '@vueuse/core'
import { readonly, shallowRef } from 'vue'
import { AppCancelError } from '@/errors'
import { isBenignError } from '@/utils/errorClassification'

export const APP_LOADING_TIMEOUT_MS = 42_000

const APP_LOADING_SPINNER_DELAY_MS = 700

export const useAppStartup = createGlobalState(() => {
  const loading = shallowRef(false)
  const error = shallowRef<unknown>(null)

  let pending = true
  let started = false

  function fail(reason: unknown): void {
    if (!pending) return

    finish()
    error.value = reason
  }

  const { start: startLoadingTimer, stop: stopLoadingTimer } = useTimeoutFn(
    () => {
      if (pending) loading.value = true
    },
    APP_LOADING_SPINNER_DELAY_MS,
    { immediate: false },
  )

  const { start: startDeadlineTimer, stop: stopDeadlineTimer } = useTimeoutFn(
    () => fail(new Error('Application loading timed out')),
    APP_LOADING_TIMEOUT_MS,
    { immediate: false },
  )

  function finish(): void {
    pending = false
    stopLoadingTimer()
    stopDeadlineTimer()
    loading.value = false
  }

  function start(): void {
    if (!pending || started) return

    started = true
    startLoadingTimer()
    startDeadlineTimer()
  }

  function reportNetworkError(
    reason: unknown,
    response: Response | undefined,
    signal?: AbortSignal,
  ): boolean {
    if (
      !pending ||
      !reason ||
      response !== undefined ||
      signal?.aborted ||
      reason instanceof AppCancelError ||
      isBenignError(reason)
    ) {
      return false
    }

    fail(reason)
    return true
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    start,
    finish,
    fail,
    reportNetworkError,
  }
})
